import {
  ButtonDS,
  DialogModalDS,
  DownloadOutlined,
  TextCapitalized,
  uppercaseFirstLetter,
} from "@qivia/ui";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styled from "styled-components";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  formatCurrency,
  formatDate,
  formatDateWithoutDay,
} from "../../../../libSlice/materialUiFormats";
import { useAppDispatch } from "../../../../redux/hooks";
import {
  GetHomeChargingSupportingDocumentsPayload,
  HomeChargingSupportingDocument,
} from "../homeChargingSupportingDocumentsAPI";
import "../../../../i18n/i18n";

type SupportingDocumentChargeRow = {
  uuid: string;
  registrationNumber: string;
  stopAt: string;
  closedAt: string;
  totalEnergy: number;
  ratePerKwh: string;
  reimbursementAmount: string;
};

export const SupportingDocumentChargesModal = (props: {
  visible: boolean;
  onClose: () => void;
  supportingDocument: HomeChargingSupportingDocument;
  period: GetHomeChargingSupportingDocumentsPayload;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const chargesExcelColumns = useMemo(
    () =>
      [
        "stopAt",
        "closedAt",
        "registrationNumber",
        "totalEnergy",
        "ratePerKwh",
        "reimbursementAmount",
      ] as (keyof SupportingDocumentChargeRow)[],
    [],
  );

  const chargesRows = useMemo(
    () =>
      props.supportingDocument.charges.map((charge) => ({
        uuid: charge.uuid,
        registrationNumber: charge.registrationNumber,
        stopAt: formatDate(charge.stopAt),
        closedAt: formatDate(charge.closedAt),
        totalEnergy: charge.totalEnergy,
        ratePerKwh: formatCurrency(charge.ratePerKwh),
        reimbursementAmount: formatCurrency(charge.reimbursementAmount),
      })),
    [props.supportingDocument.charges],
  );

  const handleDownloadCharges = () => {
    if (chargesRows.length <= 0) return;
    const headerTitles = chargesExcelColumns.map((key) =>
      uppercaseFirstLetter(
        t(`home_charging_supporting_documents.chargesModal.column.${key}`),
      ),
    );

    const file = createFileXlsx({
      tabTitle: t("home_charging_supporting_documents.chargesModal.title"),
      headerTitles,
      body: chargesRows.map((charge) =>
        chargesExcelColumns.map((column) => charge[column]),
      ),
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName:
          "QIVIA_" +
          t("home_charging_supporting_documents.chargesModal.excel.title", {
            firstName: props.supportingDocument.firstName,
            lastName: props.supportingDocument.lastName,
            period: formatDateWithoutDay(props.period.from),
          }) +
          ".xlsx",
      }),
    );
  };

  return (
    <DialogModalDS
      visible={props.visible}
      onClose={props.onClose}
      title={t("home_charging_supporting_documents.chargesModal.title")}
      width={72}
      bottomButton={
        <ButtonDS
          sizeButton="S"
          format="fill"
          buttonType="secondary"
          text={t("home_charging_supporting_documents.chargesModal.download")}
          leftIcon={<DownloadOutlined />}
          onClick={handleDownloadCharges}
        />
      }
    >
      <StyledModalContent>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                {chargesExcelColumns.map((column) => (
                  <th key={column}>
                    <StyledHeaderCell>
                      <TextCapitalized>
                        {t(
                          `home_charging_supporting_documents.chargesModal.column.${column}`,
                        )}
                      </TextCapitalized>
                    </StyledHeaderCell>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chargesRows.map((charge) => (
                <tr key={charge.uuid}>
                  {chargesExcelColumns.map((column) => (
                    <td key={column}>{charge[column]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  width: 100%;
`;

const StyledTableWrapper = styled.div`
  overflow: auto;
  max-height: 25rem;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${typographies["Body/S"]}
  color: ${colors["colors/text/black"]};

  th,
  td {
    border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
    padding: 0.75rem;
    text-align: left;
  }
`;

const StyledHeaderCell = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;
